import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  effectiveTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Get theme from localStorage or default to 'system'
    const stored = localStorage.getItem('theme') as Theme;
    return stored || 'system';
  });

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(() => {
    // Initialize theme class synchronously before first render
    const stored = localStorage.getItem('theme') as Theme;
    const currentTheme = stored || 'system';

    let initialEffectiveTheme: 'light' | 'dark' = 'light';

    if (currentTheme === 'system') {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      initialEffectiveTheme = systemPrefersDark ? 'dark' : 'light';
    } else {
      initialEffectiveTheme = currentTheme;
    }

    // Apply the class immediately to avoid flash
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(initialEffectiveTheme);

    return initialEffectiveTheme;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    // Determine the effective theme
    let newEffectiveTheme: 'light' | 'dark' = 'light';

    if (theme === 'system') {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      newEffectiveTheme = systemPrefersDark ? 'dark' : 'light';
    } else {
      newEffectiveTheme = theme;
    }

    setEffectiveTheme(newEffectiveTheme);

    // Apply or remove the dark class
    root.classList.remove('light', 'dark');
    root.classList.add(newEffectiveTheme);
  }, [theme]);

  useEffect(() => {
    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (theme === 'system') {
        const newEffectiveTheme = mediaQuery.matches ? 'dark' : 'light';
        setEffectiveTheme(newEffectiveTheme);

        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(newEffectiveTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    // Poll for system preference changes that don't fire events
    // (e.g., macOS Auto mode transitions based on time of day)
    if (theme !== 'system') return;

    let previousPreference = window.matchMedia('(prefers-color-scheme: dark)').matches;

    const intervalId = setInterval(() => {
      const currentPreference = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (currentPreference !== previousPreference) {
        previousPreference = currentPreference;
        const newEffectiveTheme = currentPreference ? 'dark' : 'light';
        setEffectiveTheme(newEffectiveTheme);

        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(newEffectiveTheme);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(intervalId);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    localStorage.setItem('theme', newTheme);
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, effectiveTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
